import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { message } from "antd";
import Layout from "./common/Layout";
import Loading from "./common/Loading";
import { edit_account_details } from "../apis/EditAccountDetails";
import { get_account_details } from "../apis/GetAccountDetails";
import "../css/applyEcommerceAccount.scss";
import { setAPIAuthorizationToken } from "../apis/API";
import Cookies from "js-cookie";
import { get_user } from "../apis/GetUser";

const ApplyEcommerceAccount = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const accountId = searchParams.get("id");
  const isEditMode = !!accountId;

  const [formData, setFormData] = useState({
    platformType: "",
    platformName: "",
    storeName: "",
    storeUrl: "",
    accountNumber: "",
    actualNumber: "",
    bankLocationCountry: "",
    iban: "",
    bicCode: "",
    bankName: "",
    bankSortCode: "",
    currency: "",
    availableBalance: "",
    remark: "",
  });
  const [userId, setUserId] = useState(null); // 存储用户ID
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [userOptions, setUserOptions] = useState([]); // 用户下拉选项

  // 初始化token
  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      setAPIAuthorizationToken(token);
    }
    // 默认选择当前用户（新增模式）
    if (!isEditMode) {
      const userCookie = Cookies.get("user");
      if (userCookie) {
        try {
          const currentUser = JSON.parse(userCookie);
          if (currentUser?.id) {
            setUserId(String(currentUser.id));
          }
        } catch (err) {
          console.error("解析当前用户信息失败:", err);
        }
      }
    }
  }, [isEditMode]);

  // 加载用户列表用于分配
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await get_user({ page: 1, page_size: 1000 });
        const list = res?.data?.data?.list || [];
        const options = list.map((u) => {
          const name = u.username || u.email || `用户${u.id}`;
          return {
            value: String(u.id),
            label: `${u.id}-${name}`,
          };
        });
        setUserOptions(options);
      } catch (err) {
        console.error("获取用户列表失败:", err);
        setUserOptions([]);
      }
    };
    fetchUsers();
  }, []);

  // 平台类型选项
  const platformTypes = [
    { value: "ecommerce", label: "电商平台" },
    { value: "service", label: "服务" },
    { value: "other", label: "其他" },
  ];

  // 平台类型映射：将后端返回的中文映射为前端value
  const mapPlatformTypeToValue = (platformType) => {
    if (!platformType) return "";
    const typeMap = {
      电商平台: "ecommerce",
      服务: "service",
      其他: "other",
    };
    return typeMap[platformType] || platformType; // 如果已经是英文value则直接返回
  };

  // 币种选项
  const currencies = [
    { value: "USD", label: "USD - 美元" },
    { value: "GBP", label: "GBP - 英镑" },
    { value: "EUR", label: "EUR - 欧元" },
    { value: "CAD", label: "CAD - 加元" },
    { value: "AUD", label: "AUD - 澳元" },
    { value: "JPY", label: "JPY - 日元" },
    { value: "HKD", label: "HKD - 港币" },
    { value: "SGD", label: "SGD - 新加坡元" },
  ];

  // 加载账户详情（编辑模式）
  useEffect(() => {
    if (isEditMode) {
      loadAccountDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const loadAccountDetails = async () => {
    setInitialLoading(true);
    try {
      const response = await get_account_details(accountId);
      if (response.data && response.data.data) {
        const account = response.data.data;
        // 保存 user_id
        setUserId(account.user_id);
        setFormData({
          platformType: mapPlatformTypeToValue(account.platform_type),
          platformName: account.platform_name || "",
          storeName: account.shop_name || "",
          storeUrl: account.shop_url || "",
          accountNumber: account.account_number || "",
          actualNumber: account.actual_number || "",
          bankLocationCountry: account.bank_location_country || "",
          iban: account.iban || "",
          bicCode: account.bic_code || "",
          bankName: account.bank_name || "",
          bankSortCode: account.bank_sort_code || "",
          currency: account.currency || "",
          availableBalance: account.available_balance || "",
          remark: account.remark || "",
        });
      } else {
        message.error(response.data?.message || "获取账户信息失败");
        navigate("/my-account");
      }
    } catch (error) {
      console.error("获取账户详情失败:", error);
      message.error("获取账户信息失败，请稍后重试");
      navigate("/my-account");
    } finally {
      setInitialLoading(false);
    }
  };

  const handleBack = () => {
    navigate("/my-account");
  };

  // 处理表单输入变化
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // 提交申请
  const handleSubmitApplication = async (e) => {
    e.preventDefault();

    // 验证必填字段
    if (
      !formData.platformType ||
      !formData.storeName ||
      !formData.currency ||
      !formData.accountNumber ||
      formData.availableBalance === ""
    ) {
      message.error("请填写所有必填字段");
      return;
    }

    setLoading(true);

    try {
      // 构造提交数据，映射到后端需要的字段名
      const accountData = {
        platform_type: formData.platformType,
        platform_name: formData.platformName,
        shop_name: formData.storeName,
        shop_url: formData.storeUrl,
        account_number: formData.accountNumber,
        actual_number: formData.actualNumber,
        bank_location_country: formData.bankLocationCountry,
        iban: formData.iban,
        bic_code: formData.bicCode,
        bank_name: formData.bankName,
        bank_sort_code: formData.bankSortCode,
        currency: formData.currency,
        available_balance: formData.availableBalance,
        remark: formData.remark,
        user_id: userId ? String(userId) : undefined,
      };

      // 如果是编辑模式，添加账户ID和用户ID
      if (isEditMode) {
        accountData.id = accountId;
        accountData.user_id = userId ? String(userId) : undefined;
      }

      const response = await edit_account_details(accountData);

      if (response.data.data) {
        message.success(
          isEditMode ? "账户信息已更新！" : "申请成功！账户已提交审核"
        );
        // 跳转回我的账户页面
        navigate("/my-account");
      } else {
        message.error(
          response.data.message ||
            (isEditMode ? "更新失败，请重试" : "申请失败，请重试")
        );
      }
    } catch (error) {
      console.error("提交申请失败：", error);
      message.error("申请失败，请稍后重试");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="apply-ecommerce-account">
        {/* 头部标题 */}
        <div className="detail-header">
          <button className="btn-back" onClick={handleBack}>
            ←
          </button>
          <h1 className="page-title">
            {isEditMode ? "编辑电商收款账户" : "申请电商收款账户"}
          </h1>
          <div className="header-placeholder"></div>
        </div>

        {initialLoading ? (
          <Loading />
        ) : (
          <div className="form-container">
            {/* 申请表单 */}
            <form onSubmit={handleSubmitApplication} className="apply-form">
              <div className="form-section">
                <h2 className="section-title">基本信息</h2>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> 平台类型
                    </label>
                    <select
                      name="platformType"
                      value={formData.platformType}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    >
                      <option value="">请选择平台类型</option>
                      {platformTypes.map((platform) => (
                        <option key={platform.value} value={platform.value}>
                          {platform.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> 币种
                    </label>
                    <select
                      name="currency"
                      value={formData.currency}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    >
                      <option value="">请选择币种</option>
                      {currencies.map((currency) => (
                        <option key={currency.value} value={currency.value}>
                          {currency.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">平台名称（自定义）</label>
                    <input
                      type="text"
                      name="platformName"
                      value={formData.platformName}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="例如：英国本土店-GBP"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label"><span className="required">*</span> 可用余额</label>
                    <input
                      type="number"
                      name="availableBalance"
                      value={formData.availableBalance}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="请输入可用余额"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> 分配用户
                    </label>
                    <select
                      name="assignUser"
                      value={userId ? String(userId) : ""}
                      onChange={(e) => setUserId(e.target.value)}
                      className="form-input"
                      required
                    >
                      <option value="">请选择用户</option>
                      {userOptions.map((u) => (
                        <option key={u.value} value={u.value}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h2 className="section-title">店铺信息</h2>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> 店铺名称
                    </label>
                    <input
                      type="text"
                      name="storeName"
                      value={formData.storeName}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="请输入店铺名称"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">店铺URL</label>
                    <input
                      type="url"
                      name="storeUrl"
                      value={formData.storeUrl}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="请输入店铺网址（选填）"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      <span className="required">*</span> 账户编号
                    </label>
                    <input
                      type="text"
                      name="accountNumber"
                      value={formData.accountNumber}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="请输入账户编号"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">备注</label>
                    <input
                      type="text"
                      name="remark"
                      value={formData.remark}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="请输入备注信息（选填）"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h2 className="section-title">银行信息</h2>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">银行账号</label>
                    <input
                      type="text"
                      name="actualNumber"
                      value={formData.actualNumber}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="请输入实际账号（选填）"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">银行所在国家</label>
                    <input
                      type="text"
                      name="bankLocationCountry"
                      value={formData.bankLocationCountry}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="请输入银行所在国家（选填）"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">IBAN</label>
                    <input
                      type="text"
                      name="iban"
                      value={formData.iban}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="请输入IBAN（选填）"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">BIC Code</label>
                    <input
                      type="text"
                      name="bicCode"
                      value={formData.bicCode}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="请输入BIC Code（选填）"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">银行名称</label>
                    <input
                      type="text"
                      name="bankName"
                      value={formData.bankName}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="请输入银行名称（选填）"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Bank Sort Code</label>
                    <input
                      type="text"
                      name="bankSortCode"
                      value={formData.bankSortCode}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="请输入Bank Sort Code（选填）"
                    />
                  </div>
                </div>
              </div>

              {/* 提交按钮 */}
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={handleBack}
                  disabled={loading}
                >
                  取消
                </button>
                <button type="submit" className="btn-submit" disabled={loading}>
                  {loading
                    ? isEditMode
                      ? "保存中..."
                      : "提交中..."
                    : isEditMode
                    ? "保存修改"
                    : "提交申请"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      <footer className="page-footer">
        <p>© 2022 GLOBAL E-PAYMENT LIMITED. All Rights Reserved.</p>
      </footer>
    </Layout>
  );
};

export default ApplyEcommerceAccount;
